import { Injectable } from '@angular/core';
import { combineLatest, distinctUntilChanged, EMPTY, Observable, of } from 'rxjs';
import { Firestore } from '@angular/fire/firestore';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { selectCurrentUser } from '../../current-user/store/current-user.selectors';
import { inputIsNotNullOrUndefined } from '../../data-access/input-is-not-null-or-undefined';
import { getCompanyUsers$, getUsersByIds$, User, withInfoForUsers$ } from '../../data-access/users';
import { isArray, isString, uniq } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class GetRelatedUsersService {

  constructor(private firestore: Firestore, private store$: Store) {
  }

  get(): Observable<User[]> {
    return this.store$.pipe(
      select(selectCurrentUser),
      filter(inputIsNotNullOrUndefined),
      switchMap(currentUser => getCompanyUsers$(this.firestore, currentUser)),
      switchMap(users => {
        const userIds = users.map(user => user.id);
        const extraUserIds = uniq(users.map(user =>
          isArray(user.reportToId) ? user.reportToId : isString(user.reportToId) ? [ user.reportToId ] : []
        ).flat().filter(uid => !users.some(u => u.id === uid)));

        return combineLatest([
          of(users),
          ...(extraUserIds.length > 0 ? [ getUsersByIds$(this.firestore, extraUserIds) ] : []),
        ]).pipe(
          map(data => data.flat()),
          switchMap(users => combineLatest([ withInfoForUsers$(this.firestore, users), of(userIds) ])),
          catchError(err => {
            // eslint-disable-next-line no-console
            console.info('Removing user process...', err);
            return EMPTY;
          }),
          distinctUntilChanged(([ prev ], [ curr ]) => JSON.stringify(prev) === JSON.stringify(curr)),
          map(([ allUserWithInfo, userIds ]) => {
            return allUserWithInfo.map(userWithInfo => {
              return {
                ...userWithInfo,
                isSuperUser: userWithInfo.id === userWithInfo.accountId,
                activated: !userWithInfo.disabled,
                reportTo: allUserWithInfo.find(u => {
                  const reportToId = userWithInfo.reportToId;
                  if (isArray(reportToId)) return reportToId.includes(u.id);
                  return u.id === reportToId;
                }) || null,
                updatedBy: allUserWithInfo.find(u => u.id === userWithInfo.updatedById),
              };
            }).filter(userWithInfo => userIds.some(uid => uid === userWithInfo.id));
          }),
        );
      }),
    );
  }
}
